/**
 * Admin Service
 * Manages allowed users and access control for the Flow application.
 *
 * Who counts as an admin is decided by the DATABASE — allowed_users.role='admin',
 * surfaced through check_flow_access().is_admin (migration 181) and enforced by
 * the allowed_users_insert/_update/_delete RLS policies. There is deliberately no
 * hardcoded admin list in the client any more: the old src/lib/admin.ts list had
 * drifted from the roster, so the UI offered admin controls to an account the
 * database then rejected.
 */

import { supabase } from '@/lib/supabase';
import type { AiImportSettingsRow } from '../../supabase/functions/_shared/ai-import-settings';

// =============================================================================
// TYPES
// =============================================================================

export interface AllowedUser {
  id: string;
  email: string;
  name: string | null;
  role: 'admin' | 'member';
  is_active: boolean;
  /** "Can email Flow": may email flow@ for analysis or enrichment (the trusted lane). Only counts on an active row. */
  can_submit_ai_imports: boolean;
  added_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface AddUserInput {
  email: string;
  name?: string;
  role?: 'admin' | 'member';
}

/** What an admin can change on an existing allowed_users row. */
export interface AllowedUserUpdates {
  name?: string;
  role?: 'admin' | 'member';
  can_submit_ai_imports?: boolean;
}

// =============================================================================
// ACCESS CHECK
// =============================================================================

export type FlowAccessStatus = 'allowed' | 'denied' | 'unverifiable';

interface FlowAccessRow {
  has_email_claim?: boolean;
  allowed?: boolean;
  is_admin?: boolean;
}

/**
 * The full answer from check_flow_access(): the entry gate AND whether this
 * session is an admin. Both come from one RPC because the app already makes
 * that call once per session — the admin answer rides along for free.
 *
 * `isAdmin` is deliberately separate from `status`. An allow-listed non-admin
 * is fully 'allowed'; admin is an extra capability, not a higher access tier.
 */
export interface FlowAccess {
  status: FlowAccessStatus;
  isAdmin: boolean;
}

/**
 * Pure decision from check_flow_access()'s payload. Fail-safe: a hard denial
 * requires a token that carries an email genuinely absent from the allowlist.
 * A missing email claim means the session token didn't identify the user (a
 * Microsoft-SSO → Supabase token-exchange hiccup) — that's 'unverifiable',
 * recoverable by re-auth, never a hard lockout of a legitimate user.
 */
export function classifyFlowAccess(row: FlowAccessRow | null | undefined): FlowAccessStatus {
  if (!row || row.has_email_claim !== true) return 'unverifiable';
  return row.allowed === true ? 'allowed' : 'denied';
}

/**
 * Fail-safe access check via the check_flow_access() RPC (migration 119).
 * Throws on transport/RPC failure so react-query retries and the UI shows a
 * "verifying…" retry state rather than a hard "Access Restricted".
 *
 * Requires a Supabase session. The RPC classifies access from the caller's JWT
 * email, so without a session it runs on the anon key and returns a spurious
 * 'unverifiable'. React Query would cache that as a *successful* result (no
 * throw) for staleTime, so a session arriving moments later wouldn't dislodge
 * it — reviving the "Couldn't verify your access" screen. Throwing when there's
 * no session keeps that false negative out of the cache entirely: it never
 * becomes sticky data, and the query re-runs cleanly once a session exists.
 * useAccessCheck also gates `enabled` on the session, but React Query v5's
 * refetch() bypasses `enabled`, so the invariant must live here too.
 */
export async function checkFlowAccess(): Promise<FlowAccess> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('checkFlowAccess: no Supabase session yet');

  const { data, error } = await supabase.rpc('check_flow_access');
  if (error) throw error;
  const row = data as FlowAccessRow | null;
  return {
    status: classifyFlowAccess(row),
    // Strict true: a missing key (an older database that predates migration 181)
    // reads as "not admin" rather than as admin, so a stale server can only ever
    // under-grant the admin UI, never over-grant it.
    isAdmin: row?.is_admin === true,
  };
}

// =============================================================================
// ADMIN OPERATIONS
// =============================================================================

/**
 * Get all allowed users
 */
export async function getAllowedUsers(): Promise<AllowedUser[]> {
  const { data, error } = await supabase
    .from('allowed_users')
    .select('*')
    .order('role', { ascending: true })
    .order('created_at', { ascending: true });

  if (error) {
    console.error('[Admin] Error fetching allowed users:', error);
    throw new Error(error.message);
  }

  return data || [];
}

/**
 * The result of provisioning someone's Supabase auth account. Deliberately a
 * value, not an exception: a provisioning hiccup must not make a successful
 * allowlist insert look like a failure.
 */
export type ProvisionResult =
  | { outcome: 'created' | 'exists' }
  | { outcome: 'failed'; error: string };

/**
 * Create the Supabase auth user behind an allowlist entry.
 *
 * Allowlist membership alone doesn't let anyone in: the project runs with
 * signups disabled, so gotrue refuses to create an account during the
 * Microsoft→Supabase token exchange and the user is stranded on the access
 * gate. Provisioning is what makes the authorization real. Idempotent — an
 * address that already has an account reports 'exists'.
 */
export async function provisionAuthUser(email: string): Promise<ProvisionResult> {
  try {
    const { data, error } = await supabase.functions.invoke('provision-auth-user', {
      body: { email },
    });
    if (error) return { outcome: 'failed', error: error.message };
    const body = data as { ok?: boolean; outcome?: string; error?: string } | null;
    if (!body?.ok) {
      return { outcome: 'failed', error: body?.error ?? 'Provisioning failed' };
    }
    return { outcome: body.outcome === 'created' ? 'created' : 'exists' };
  } catch (err) {
    return {
      outcome: 'failed',
      error: err instanceof Error ? err.message : 'Provisioning failed',
    };
  }
}

/**
 * Add a user to the allowed list
 */
export async function addAllowedUser(
  input: AddUserInput,
  addedByEmail: string
): Promise<AllowedUser> {
  const { data, error } = await supabase
    .from('allowed_users')
    .insert({
      email: input.email.toLowerCase().trim(),
      name: input.name?.trim() || null,
      role: input.role || 'member',
      added_by: addedByEmail,
    })
    .select()
    .single();

  if (error) {
    if (error.code === '23505') {
      throw new Error('This email is already in the access list.');
    }
    console.error('[Admin] Error adding user:', error);
    throw new Error(error.message);
  }

  return data;
}

/**
 * Remove a user from the allowed list (soft delete - deactivate)
 */
export async function removeAllowedUser(id: string): Promise<void> {
  const { error } = await supabase
    .from('allowed_users')
    .update({ is_active: false })
    .eq('id', id);

  if (error) {
    console.error('[Admin] Error removing user:', error);
    throw new Error(error.message);
  }
}

/**
 * Reactivate a previously removed user
 */
export async function reactivateUser(id: string): Promise<void> {
  const { error } = await supabase
    .from('allowed_users')
    .update({ is_active: true })
    .eq('id', id);

  if (error) {
    console.error('[Admin] Error reactivating user:', error);
    throw new Error(error.message);
  }
}

/**
 * Update a user's details
 */
export async function updateAllowedUser(
  id: string,
  updates: AllowedUserUpdates
): Promise<AllowedUser> {
  const { data, error } = await supabase
    .from('allowed_users')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('[Admin] Error updating user:', error);
    throw new Error(error.message);
  }

  return data;
}

/**
 * Permanently delete a user from the list
 */
export async function deleteAllowedUser(id: string): Promise<void> {
  const { error } = await supabase
    .from('allowed_users')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('[Admin] Error deleting user:', error);
    throw new Error(error.message);
  }
}

// =============================================================================
// INBOUND EMAIL — BLOCKLIST + INTAKE SETTINGS (migration 146)
// =============================================================================

export type BlockMatchType = 'email' | 'domain';
export type BlockReason = 'spam' | 'abuse' | 'injection_attempt' | 'cost_abuse' | 'vendor_noise' | 'manual';

export interface BlocklistEntry {
  id: string;
  match_type: BlockMatchType;
  value_normalized: string;
  reason: BlockReason;
  note: string | null;
  is_active: boolean;
  expires_at: string | null;
  created_by: string | null;
  created_at: string;
}

export interface AddBlockInput {
  match_type: BlockMatchType;
  value: string;
  reason: BlockReason;
  note?: string;
}

// Mirror of migration 146's DB-level guard so the UI fails fast with a friendly message; the
// CHECK constraint is the real enforcement (a SQL insert can't bypass it either).
const FREEMAIL_DOMAINS = new Set([
  'gmail.com', 'googlemail.com', 'outlook.com', 'hotmail.com', 'live.com', 'msn.com',
  'yahoo.com', 'ymail.com', 'icloud.com', 'me.com', 'mac.com', 'aol.com',
  'proton.me', 'protonmail.com', 'gmx.com', 'mail.com', 'zoho.com',
]);

/** lower/trim; for a domain also strip a leading '@'. */
function normalizeBlockValue(matchType: BlockMatchType, value: string): string {
  const v = value.toLowerCase().trim();
  return matchType === 'domain' ? v.replace(/^@+/, '') : v;
}

export async function getBlocklist(): Promise<BlocklistEntry[]> {
  const { data, error } = await supabase
    .from('inbound_sender_blocklist')
    .select('*')
    .order('is_active', { ascending: false })
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[Admin] Error fetching blocklist:', error);
    throw new Error(error.message);
  }
  return data || [];
}

export async function addBlockEntry(input: AddBlockInput): Promise<BlocklistEntry> {
  const value_normalized = normalizeBlockValue(input.match_type, input.value);
  if (!value_normalized) throw new Error('Enter an email address or domain to block.');
  if (input.match_type === 'email' && !value_normalized.includes('@')) {
    throw new Error('That is not an email address. Choose match type "Domain" to block a whole domain.');
  }
  if (input.match_type === 'domain' && (value_normalized.includes('@') || !value_normalized.includes('.'))) {
    throw new Error('Enter a bare domain, e.g. "spammer.com".');
  }
  if (input.match_type === 'domain' && FREEMAIL_DOMAINS.has(value_normalized)) {
    throw new Error(`Can't block the whole ${value_normalized} domain — it would drop legitimate senders. Block the specific address instead.`);
  }

  const { data, error } = await supabase
    .from('inbound_sender_blocklist')
    .insert({
      match_type: input.match_type,
      value_normalized,
      reason: input.reason,
      note: input.note?.trim() || null,
    })
    .select()
    .single();

  if (error) {
    if (error.code === '23505') throw new Error('That sender or domain is already on the blocklist.');
    console.error('[Admin] Error adding block entry:', error);
    throw new Error(error.message);
  }
  return data;
}

// The `.select()` + row-count check turns a 0-row RLS-blocked write (which PostgREST reports as
// success with no rows) into a visible error instead of a silent no-op.
export async function deactivateBlockEntry(id: string): Promise<void> {
  const { data, error } = await supabase.from('inbound_sender_blocklist').update({ is_active: false }).eq('id', id).select('id');
  if (error) {
    console.error('[Admin] Error deactivating block entry:', error);
    throw new Error(error.message);
  }
  if (!data || data.length === 0) throw new Error('Change was blocked — you may not have admin permission.');
}

export async function reactivateBlockEntry(id: string): Promise<void> {
  const { data, error } = await supabase.from('inbound_sender_blocklist').update({ is_active: true }).eq('id', id).select('id');
  if (error) {
    console.error('[Admin] Error reactivating block entry:', error);
    throw new Error(error.message);
  }
  if (!data || data.length === 0) throw new Error('Change was blocked — you may not have admin permission.');
}

export async function deleteBlockEntry(id: string): Promise<void> {
  const { data, error } = await supabase.from('inbound_sender_blocklist').delete().eq('id', id).select('id');
  if (error) {
    console.error('[Admin] Error deleting block entry:', error);
    throw new Error(error.message);
  }
  if (!data || data.length === 0) throw new Error('Delete was blocked — you may not have admin permission.');
}

/** What an unknown sender's mail becomes: `lead` is the migration-146 regex lead; `analyze` triages it and runs the AI. */
export type OpenLaneMode = 'lead' | 'analyze';

export interface InboundSettings {
  id: string;
  accept_unknown_senders: boolean;
  auto_ack_enabled: boolean;
  ack_template: string;
  notification_recipients: string[];
  per_sender_daily_cap: number;
  global_hourly_cap: number;
  // Migration 260 — the open lane. `open_lane_mode` only counts while accept_unknown_senders is on.
  open_lane_mode: OpenLaneMode;
  /** Who receives open-lane analyses. The send path intersects this with the ACTIVE team at send time. */
  open_lane_report_recipients: string[];
  /** Max open-lane jobs per rolling 24 h. DB CHECK: >= 0. */
  open_lane_daily_job_cap: number;
  /** Per open-lane job per rolling 24 h: max reply-driven re-runs and max reply-acks. DB CHECK: >= 0. */
  open_lane_reply_budget: number;
  /** The "reach out to us directly" address in the decline email (goes into a mailto: link). */
  open_lane_contact_email: string;
  updated_at: string;
}

export type InboundSettingsUpdate = Partial<Omit<InboundSettings, 'id' | 'updated_at'>>;

export async function getInboundSettings(): Promise<InboundSettings | null> {
  const { data, error } = await supabase.from('inbound_settings').select('*').limit(1).maybeSingle();
  if (error) {
    console.error('[Admin] Error fetching inbound settings:', error);
    throw new Error(error.message);
  }
  return data;
}

export async function updateInboundSettings(updates: InboundSettingsUpdate): Promise<InboundSettings> {
  const { data, error } = await supabase
    .from('inbound_settings')
    .update(updates)
    .eq('is_singleton', true)
    .select()
    .single();

  if (error) {
    console.error('[Admin] Error updating inbound settings:', error);
    throw new Error(error.message);
  }
  return data;
}

// =============================================================================
// AI-IMPORT STAGE SWITCHES (ai_import_settings, migration 278)
// =============================================================================

/**
 * The one ai_import_settings row: the pipeline's stage switches, read by the worker, submit, the report and
 * apply through supabase/functions/_shared/ai-import-settings.ts (the columns and their defaults live there).
 * Read by any allowed user; written by admins (RLS). `auto_apply_min_confidence` is a numeric column and
 * may arrive as a string from some drivers: read the row through normalizeAiImportSettings before using it.
 */
export interface AiImportSettings extends AiImportSettingsRow {
  id: string;
  updated_at: string;
  updated_by: string | null;
}

export type AiImportSettingsUpdate = Partial<AiImportSettingsRow>;

export async function getAiImportSettings(): Promise<AiImportSettings | null> {
  const { data, error } = await supabase.from('ai_import_settings').select('*').limit(1).maybeSingle();
  if (error) {
    console.error('[Admin] Error fetching AI-import settings:', error);
    throw new Error(error.message);
  }
  return data;
}

export async function updateAiImportSettings(updates: AiImportSettingsUpdate): Promise<AiImportSettings> {
  const { data, error } = await supabase
    .from('ai_import_settings')
    .update(updates)
    .eq('is_singleton', true)
    .select()
    .single();
  if (error) {
    console.error('[Admin] Error updating AI-import settings:', error);
    throw new Error(error.message);
  }
  return data;
}
